/**
 * Embed bootstrap — Phase 4-2 / T11.
 *
 * Wires the EmbedBridge, registers the `init` handler that performs the SSO
 * exchange, and signals `ready` back to the host. Returns the bridge + http
 * handles so caller (App.svelte) can keep them in scope.
 *
 * Importers: src/App.svelte (calls startEmbedBootstrap(apiBase) in onMount).
 * Depends on: ./bridge.js, ./protocol.js, ../api/http.js, ../api/queries.js,
 * ../stores/session.svelte.js, ../api/query-client.js.
 */

import { queryClient } from '../api/query-client.js';
import { http } from '../api/http.js';
import { ssoExchange, type SsoResponse } from '../api/queries.js';
import { session } from '../stores/session.svelte.js';

import { EmbedBridge, applyTheme, newEnvelopeId } from '@platform/embed-bridge';
import type { InitMessage, SetThemeMessage } from '@platform/embed-bridge';

export interface BootstrapHandles {
  bridge: EmbedBridge;
  api: typeof http;
}

/**
 * Start the embed bootstrap sequence. Caller should invoke once per widget
 * mount (in App.svelte onMount).
 *
 * - Creates an EmbedBridge.
 * - Registers an `init` handler that runs SSO, locks origin, sets session.
 * - Calls bridge.start() so the listener is live.
 * - Sends `ready` to the host so it knows to deliver `init`.
 */
export function startEmbedBootstrap(_apiBase: string): BootstrapHandles {
  const bridge = new EmbedBridge();

  bridge.on<InitMessage>('init', (msg, origin) => {
    void handleInit(bridge, msg, origin);
  });

  bridge.on<SetThemeMessage>('setTheme', (msg) => {
    applyTheme(msg.data.theme as Record<string, string> | undefined);
  });

  bridge.start();

  // The one unlocked outbound: the host can't send `init` — the message we
  // derive the origin lock from — until it knows the widget is listening.
  bridge.sendHandshake({
    v: 1,
    type: 'ready',
    id: newEnvelopeId(),
    data: { protocolVersion: 1 },
  });

  return { bridge, api: http };
}

async function handleInit(
  bridge: EmbedBridge,
  msg: InitMessage,
  origin: string,
): Promise<void> {
  const { hostProductCode, hostUserJwt, theme: initialTheme } = msg.data;
  try {
    const sso: SsoResponse = await queryClient.fetchQuery(
      ssoExchange({ hostProductCode, hostUserJwt }),
    );

    session.setReady(sso);
    bridge.setAllowedOrigins(sso.embedOrigins ?? []);

    // Lock to the origin that sent this `init`, not to document.referrer.
    // The referrer is suppressible by the host's Referrer-Policy, and when it
    // was missing the old code skipped locking entirely — leaving the bridge
    // broadcasting to '*' and accepting inbound from anyone. `origin` here is
    // the browser's own attribution of the sender and cannot be spoofed.
    // Unconditional: there is no path that leaves the bridge unlocked.
    try {
      bridge.lockToOrigin(origin);
    } catch (err) {
      // Origin not in the configured allowlist, or opaque. Hard error — we
      // will not serve a session we cannot safely talk to.
      session.setError(err instanceof Error ? err.message : String(err));
      return;
    }

    http.setSessionToken(sso.sessionToken ?? null);

    // Phase 6-2: apply admin-saved tenant theme first (defaults).
    if (sso.tenant?.themeTokens) {
      applyTheme(sso.tenant.themeTokens);
    }
    // Phase 4-4 / T19: apply initial theme from the `init` payload. The
    // `setTheme` handler registered above keeps applying updates on subsequent
    // host messages. Host-pushed theme overrides the tenant default.
    applyTheme(initialTheme as Record<string, string> | undefined);
  } catch (err) {
    session.setError(err instanceof Error ? err.message : String(err));
  }
}
